<template>
	<div class="filter-rating">
		<div class="filter-toolbar">
			<span class="title">{{ $t('WEBAPP_RATING') }}</span>
			<ResetButton :filterNames="['scoring_filter_types']" />
		</div>

		<div class="jw-padding">
			<div v-for="scoring in scorings" :key="scoring.provider" style="display: flex; align-items: baseline">
				<div>
					<img :src="scoring.image" loading="lazy" style="width: 24px; height: 24px" />
				</div>

				<RangeInput
					class="range"
					:modelValue="rangeRefs[scoring.provider]"
					:min="scoring.min"
					:max="scoring.max"
					:step="scoring.step"
					@update:modelValue="value => changed(scoring, value as number)"
				>
					<template #start>{{ scoring.min }}</template>
					<template #end>{{ scoring.max }}</template>
				</RangeInput>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { FilterCollection } from '@/stores/useFilterStoreTypes';

import { computed, ref, watch } from 'vue';

import RangeInput from '@/ui-components/RangeInput.vue';
import ResetButton from '@/components/buttons/ResetButton.vue';

import { FormattedScoring, SCORINGS } from '@/helpers/scoring-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useFilterStore } from '@/stores/pinia/useFilterStore';

const { currentFilters } = defineProps<{
	currentFilters: FilterCollection;
}>();

const router = useRouter();

const filterStore = useFilterStore();

const rangeRefs = ref<{ [key: string]: number }>({});

const scorings = computed<FormattedScoring[]>(() => {
	const scoringFilters = currentFilters.scoring_filter_types || {};

	return SCORINGS.filter(scoring => scoring.filterDisplay).map(scoring => {
		const scoreFilter = scoringFilters[scoring.provider];
		const value = scoreFilter?.min_scoring_value ?? 0;

		return {
			...scoring,
			value: round(value),
		};
	});
});

watch(
	scorings,
	newScorings => {
		const newProviders = new Set<string>();

		for (const scoring of newScorings) {
			newProviders.add(scoring.provider);
			if (rangeRefs.value[scoring.provider] !== scoring.value) {
				rangeRefs.value[scoring.provider] = scoring.value;
			}
		}

		Object.keys(rangeRefs.value).forEach(p => {
			if (!newProviders.has(p)) delete rangeRefs.value[p];
		});
	},
	{ deep: true, immediate: true }
);

/**
 * the range slider has changed and needs to update the store as well.
 */
function changed(scoring: FormattedScoring, val: number) {
	const value = round(val);
	rangeRefs.value[scoring.provider] = round(value);

	const filterValue = {
		...currentFilters.scoring_filter_types,
		[scoring.provider]: {
			min_scoring_value: value,
		},
	};
	if (value === scoring.min) {
		delete filterValue[scoring.provider];
	}

	const newRoute = filterStore.setFilterValue({
		filterName: 'scoring_filter_types',
		filterValue: Object.keys(filterValue).length ? filterValue : null,
	});

	TrackingHelper.trackEvent('userinteraction', {
		action: 'filter_toggled',
		label: 'scoring_filter_types',
	});

	if (newRoute) router.push(newRoute);
}

function round(rating: number) {
	return Math.round(rating * 10) / 10;
}
</script>

<style lang="scss">
.filter-rating {
	.range {
		flex-grow: 1;
	}
}
</style>
